import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Search,
  RefreshCw,
  Loader2,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
} from 'lucide-react'
import { TemplateCard } from './template-card'
import { TemplatePreview } from './template-preview'
import {
  useWATemplates,
  useWATemplateStats,
  useSyncWATemplates,
  useDeleteWATemplate,
} from '@/hooks/useWATemplates'
import type {
  WATemplate,
  WATemplateStatus,
  WATemplateCategory,
  WATemplatesListParams,
} from '@/lib/api-client'

export function TemplateList() {
  const navigate = useNavigate()

  // State
  const [searchQuery, setSearchQuery] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<WATemplateStatus | 'all'>('all')
  const [categoryFilter, setCategoryFilter] = React.useState<WATemplateCategory | 'all'>('all')
  const [page, setPage] = React.useState(1)
  const [selectedTemplate, setSelectedTemplate] = React.useState<WATemplate | null>(null)
  const [previewOpen, setPreviewOpen] = React.useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [templateToDelete, setTemplateToDelete] = React.useState<WATemplate | null>(null)

  // Build query params
  const params: WATemplatesListParams = {
    page,
    limit: 12,
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    sortBy: 'name',
    sortOrder: 'asc',
  }

  // Queries
  const { data, isPending, isError, refetch } = useWATemplates(params)
  const { data: stats } = useWATemplateStats()
  const syncMutation = useSyncWATemplates()
  const deleteMutation = useDeleteWATemplate()

  const templates = data?.data || []
  const pagination = data?.pagination

  // Handlers
  const handleSync = () => {
    syncMutation.mutate()
  }

  const handleView = (template: WATemplate) => {
    setSelectedTemplate(template)
    setPreviewOpen(true)
  }

  const handleEdit = (template: WATemplate) => {
    setSelectedTemplate(template)
    setPreviewOpen(true)
  }

  const handleDeleteClick = (template: WATemplate) => {
    setTemplateToDelete(template)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteMutation.mutate(templateToDelete.id, {
        onSuccess: () => {
          setDeleteConfirmOpen(false)
          setTemplateToDelete(null)
        },
      })
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1) // Reset to first page on search
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as WATemplateStatus | 'all')
    setPage(1)
  }

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value as WATemplateCategory | 'all')
    setPage(1)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - consistent with other pages */}
      <div className="flex-shrink-0 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 border-b border-border bg-page">
        {/* Filters Row - consistent with Patients page */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-auto sm:min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-9 h-10 text-sm bg-card border border-border rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-10 text-sm bg-card border border-border min-w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>

            <Select value={categoryFilter} onValueChange={handleCategoryChange}>
              <SelectTrigger className="h-10 text-sm bg-card border border-border min-w-[140px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="MARKETING">Marketing</SelectItem>
                <SelectItem value="UTILITY">Utility</SelectItem>
                <SelectItem value="AUTHENTICATION">Authentication</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={() => navigate({ to: '/whatsapp-templates/new' })}
              variant="default"
              size="default"
              className="h-10"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Create</span>
              <span className="sm:hidden">New</span>
            </Button>

            <Button
              onClick={handleSync}
              disabled={syncMutation.isPending}
              variant="outline"
              size="default"
              className="h-10"
            >
              {syncMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              <span className="hidden sm:inline">Sync from Meta</span>
              <span className="sm:hidden">Sync</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Sync Result Banner */}
      {syncMutation.isSuccess && (
        <div className="px-3 sm:px-4 lg:px-6 py-2 bg-success/10 border-b border-success/20">
          <p className="text-sm text-success">
            {syncMutation.data.message} - {syncMutation.data.created} new, {syncMutation.data.updated} updated
          </p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {isPending ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <TemplateCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-sm text-text-muted mb-4">Failed to load templates</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FileText className="w-12 h-12 text-text-muted mb-4" />
            <p className="text-sm text-text-muted mb-2">No templates found</p>
            <p className="text-xs text-text-muted mb-4">
              {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all'
                ? 'Try changing filters or search'
                : 'Click "Sync from Meta" to load templates'}
            </p>
            {!searchQuery && statusFilter === 'all' && categoryFilter === 'all' && (
              <Button onClick={handleSync} disabled={syncMutation.isPending}>
                {syncMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sync from Meta
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onView={() => handleView(template)}
                  onEdit={() => handleEdit(template)}
                  onDelete={() => handleDeleteClick(template)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-text-muted px-4">
                  Page {page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Sheet */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Template Preview</SheetTitle>
            <SheetDescription>
              {selectedTemplate?.name}
            </SheetDescription>
          </SheetHeader>
          {selectedTemplate && (
            <TemplatePreview
              templateId={selectedTemplate.id}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete template "{templateToDelete?.name}"?
              This only removes it from local database, not from Meta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  )
}

// Stat Card component - consistent with Analytics page style
function StatCard({
  icon,
  label,
  value,
  variant,
}: {
  icon: React.ReactNode
  label: string
  value: number
  variant?: 'success' | 'warning' | 'destructive'
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div
        className={cn(
          'flex items-center justify-center w-10 h-10 rounded-lg',
          variant === 'success' && 'bg-success/10',
          variant === 'warning' && 'bg-warning/10',
          variant === 'destructive' && 'bg-destructive/10',
          !variant && 'bg-primary/10'
        )}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-semibold text-text-dark">{value}</p>
        <p className="text-sm text-text-muted">{label}</p>
      </div>
    </div>
  )
}

// Skeleton for loading state
function TemplateCardSkeleton() {
  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-5 w-14" />
          </div>
        </div>
        <Skeleton className="h-8 w-8 rounded" />
      </div>
      <Skeleton className="h-20 w-full rounded mb-3" />
      <div className="flex items-center justify-between pt-2 border-t border-border/50">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}
